/*
 * Cliente HTTP do painel.
 *
 * - Envia sempre o cookie de sessao (credentials: 'include').
 * - Anexa o header X-CSRF-Token nas mutacoes, lendo o cookie hs_csrf.
 * - Converte erro da API em ApiRequestError com o `code` preservado, para as
 *   telas decidirem comportamento sem depender de mensagem.
 */
import type { ApiErrorCode, ApiErrorDetail, PaginationMeta } from '@hotspot/shared';

const CSRF_COOKIE = 'hs_csrf';
const SAFE_METHODS = new Set(['GET', 'HEAD']);

export class ApiRequestError extends Error {
  readonly code: ApiErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly details: ApiErrorDetail[];

  constructor(
    code: ApiErrorCode | 'NETWORK_ERROR',
    message: string,
    status: number,
    details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Mensagem pronta para exibir, agregando os detalhes de validacao. */
  get displayMessage(): string {
    if (this.details.length === 0) return this.message;
    return `${this.message}: ${this.details.map((detail) => detail.issue).join('; ')}`;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** Disparado quando a API responde 401: o AuthProvider derruba a sessao local. */
export const UNAUTHENTICATED_EVENT = 'hotspot:unauthenticated';

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    throw new ApiRequestError(
      'NETWORK_ERROR',
      'Nao foi possivel falar com o servidor. Verifique sua conexao.',
      0,
      [{ issue: error instanceof Error ? error.message : 'falha de rede' }],
    );
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | { data?: T; meta?: PaginationMeta; error?: { code: ApiErrorCode; message: string; details?: ApiErrorDetail[] } }
    | null;

  if (!response.ok) {
    const error = payload?.error;

    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    }

    throw new ApiRequestError(
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? `Erro ${response.status}`,
      response.status,
      error?.details ?? [],
    );
  }

  return payload?.data as T;
}

/** Variante para listagens paginadas, que precisam do meta. */
export async function apiList<T>(
  path: string,
): Promise<{ data: T[]; meta: PaginationMeta | undefined }> {
  const response = await fetch(`/api${path}`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });

  const payload = (await response.json().catch(() => null)) as
    | { data?: T[]; meta?: PaginationMeta; error?: { code: ApiErrorCode; message: string } }
    | null;

  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    throw new ApiRequestError(
      payload?.error?.code ?? 'INTERNAL_ERROR',
      payload?.error?.message ?? `Erro ${response.status}`,
      response.status,
    );
  }

  return { data: payload?.data ?? [], meta: payload?.meta };
}
